import { Box } from "@mui/system";
import {
  Alert, AlertColor, Button,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Colors } from "../../helpers/Consts";
import {
  IShoppingBagData,
  IShoppingBagItem,
  IShoppingBagRemoveItemParams,
  IUpdateItemShoppingBag,
} from "../../types/ShoppingBag";
import EditItemBagDialog from "../../dialogs/EditItemBagDialog";
import { Add, Delete, Remove } from "@mui/icons-material";
import SelectDeliveryMode from "./SelectDeliveryMode";
import { IPaymentTypeList } from "../../types/Payment";
import CpfTextField from "../../ui/CpfTextField";
import logger from "../Logger";
import { useFormik } from "formik";
import * as Yup from "yup";
import { cpf } from "cpf-cnpj-validator";
import { useQueryAddresses } from "../../services/api/queries/useQueryAddresses";
import { useMutationRemoveItemShoppingBag } from "../../services/api/mutations/useMutationRemoveItemShoppingBag";
import { useMutationUpdateUserOrderStatus } from "../../services/api/mutations/useMutationUpdateUserOrderStatus";
import {
  MUTATION_UPDATE_ITEM_QUERY_KEY,
  useMutationItemUpdateShoppingBag,
} from "../../services/api/mutations/useMutationUpdateItemShoppingBag";
import { MUTATION_ADD_ITEM_QUERY_KEY } from "../../services/api/mutations/useMutationAddItemShoppingBag";
import { ILoginData } from "../../types/Login";
import { useIsMutating, UseQueryResult } from "react-query";
import SelectPaymentType from "./SelectPaymentType";
import { IPlaceData } from "../../types/Place";
import DeliveryDistanceWarning from "./DeliveryDistanceWarning";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useState } from "react";

type Props = {
  shoppingBag: IShoppingBagData,
  isRefetchingShoppingBag: boolean
  loginInfo: ILoginData
  placeInfo: IPlaceData | undefined
  showSnackbarMessage: (options: {message: string; severity: AlertColor;}) => void,
  paymentTypesQuery: UseQueryResult<IPaymentTypeList>
  setOpenVideoDialog: (b:boolean)=>void
  setOpenCreateAddressDialog: (b:boolean)=>void
  setOpenEditTableNumberDialog: (b:boolean)=>void
  setOpenDialogEditProfile: (b:boolean)=>void
  setOpenChangeValueDialog: (b:boolean)=>void
  changeValue: bigint,
  setChangeValue:  (b:bigint)=>void ,
  toggleDrawer: ()=>void,
  setOpenPromptSendOrderViaWhatsApp: (b:boolean)=>void,
  setLastOrderState: (lastOrder: string) => void,
}



export default function ShoppingBagData({
  showSnackbarMessage,
  shoppingBag,
  isRefetchingShoppingBag,
  loginInfo,
  placeInfo,
  paymentTypesQuery,
  setOpenVideoDialog,
  setOpenCreateAddressDialog,
  setOpenEditTableNumberDialog,
  setOpenDialogEditProfile,
  setOpenChangeValueDialog,
  changeValue,
  setChangeValue,
  toggleDrawer,
  setOpenPromptSendOrderViaWhatsApp,
  setLastOrderState
}: Props){

  // Prevent the user from closing the tab/window
  const beforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    // Some browsers require setting returnValue
    e.returnValue = '';
    return '';
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isBlocking, setIsBlocking] = useState(false);

  const apiAddresses = useQueryAddresses(Number(loginInfo?.id));
  const { mutate: removeItemShoppingBag, isLoading: isMutatingRemoveItem } = useMutationRemoveItemShoppingBag();
  const { mutateAsync: closeUserOrderAsync, /*mutate: closeUserOrder,*/ isLoading: isMutatingUpdateStatus } = useMutationUpdateUserOrderStatus();
  const { mutate: updateItemShoppingBagMutate, isLoading: isMutatingUpdateItem } = useMutationItemUpdateShoppingBag();

  // obs: os 'useIsMutating' retornam o número de mutações em andamento
  const xisMutatingAddItemCount = useIsMutating(MUTATION_ADD_ITEM_QUERY_KEY)

  // a mutation a seguir é usada no dialog de editar item
  const isMutatingEditItem = useIsMutating(MUTATION_UPDATE_ITEM_QUERY_KEY)

  const isLoadingShoppingBag =
    isMutatingRemoveItem
  || isMutatingEditItem > 0
  || isMutatingUpdateStatus
  || isMutatingUpdateItem
  || xisMutatingAddItemCount > 0

  const formik = useFormik({
    initialValues: {
      user_document: shoppingBag.user_document ?? '',
      observations: shoppingBag.observations ?? '',
      payment_type: shoppingBag.payment_type ?? '',
      deliveryMode: (shoppingBag.table_number ?? 0  > 1) || placeInfo?.only_withdraw === true ? 'retirada' : 'delivery',
    },
    validationSchema: Yup.object().shape({
      // payment_type: Yup.number().min(1, "Escolha uma forma de pagamento."),
      // user_document: Yup.string().max(18, "O documento está muito longo.").test("CPF", "Digite um CPF válido.", (value) => cpf.isValid(value!)),
    }),
    enableReinitialize: true,
    onSubmit: async (values: any) => {
      // typescript tava reclamando
      const notNullShoppingData :IShoppingBagData = shoppingBag

      // GUARD: Se visitante, preenche os dados de perfil primeiro!
      if (loginInfo?.name === "Visitante") {
        setOpenDialogEditProfile(true);
        return;
      }

      if (
        notNullShoppingData.table_number === 0 // se NÃO é MESA
        && values.deliveryMode === 'delivery' // e SIM é DELIVERY
        && (!apiAddresses.data?.data || apiAddresses.data.data.length < 1) // e não tem endereço cadastrado
      ) {
        showSnackbarMessage({message: 'Cadastre um Endereço para fazer o pedido.', severity: 'error'});
        setTimeout(() => {
          setOpenCreateAddressDialog(true);
        }, 700);
        return;
      }

      // GUARD: algo com pagamentos, mas sem isso não pode finalizar o pedido!
      if (
        (values.payment_type === 0 || values.payment_type === null || values.payment_type === '') &&
        (notNullShoppingData.table_number === null ||
          notNullShoppingData.table_number < 1)
      ) {
        showSnackbarMessage({message: 'É necessario escolher uma forma de pagamento', severity: 'error'})
        // setOpenDialogPaymentType(true);
        await formik.setFieldTouched("payment_type", true);
        return;
      }

      // GUARD: se digitou algo no campo CPF, melhor ter certeza que é um CPF válido!
      // Se não, não pode enviar pedido!
      if (
        formik.values.user_document.length > 0 &&
        !cpf.isValid(formik.values.user_document)
      ) {
        showSnackbarMessage({message: 'Digite um CPF válido.', severity: 'error'})
        return;
      }

      notNullShoppingData.delivery_in_hands = true;
      notNullShoppingData.status = 12;
      notNullShoppingData.status_description = "";
      notNullShoppingData.observations = values.observations;
      notNullShoppingData.user_document = String(values.user_document)
        .replaceAll(".", "")
        .replaceAll("-", "");

      const enderecoAtivo = apiAddresses.data?.data?.find((address) => address.active);

      // console.log('apiAddresses.data?.data.length', apiAddresses.data?.data.length)
      if (apiAddresses.data?.data.length
        && notNullShoppingData.user_address_id !== undefined
        && enderecoAtivo !== undefined) {

        notNullShoppingData.user_address_id = enderecoAtivo.id;//apiAddresses.data.data[0].id;
        // console.log('apiAddresses.data.data[0].id;', apiAddresses.data.data[0].id)
      }
      if (
        (notNullShoppingData.table_number !== null &&
          notNullShoppingData.table_number !== 0)
        || values.deliveryMode === 'retirada'
      ) {
        notNullShoppingData.total -= notNullShoppingData.delivery_tax;
        notNullShoppingData.delivery_tax = 0;
      }

      notNullShoppingData.withdraw = values.deliveryMode === 'retirada'
      notNullShoppingData.change_value = Number(changeValue) /100;

      // if(shoppingBag)
      notNullShoppingData.payment_type = Number(values.payment_type);

      const selectedPaymentType = paymentTypesQuery?.data?.data.find((type) => type.id === Number(values.payment_type))
    //console.log('selectedPaymentType:', selectedPaymentType)

      try{
        // Build the state for WhatsApp if needed
        const state = JSON.stringify({
          placeInfo, selectedPaymentType, loginInfo, notNullShoppingData
        });


        window.addEventListener('beforeunload', beforeUnload);

        // Put the UI in a "blocking" mode so they can't navigate inside the app.
        // Add a local state, e.g. setIsBlocking(true). See overlay snippet below.
        setIsBlocking(true);

        // Wait for the mutation to finish, with a fallback timeout (e.g. 10s)
        const mutationPromise = closeUserOrderAsync(notNullShoppingData);

        const result = await Promise.race([
          mutationPromise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
        ]);

        // If we reach here the mutation resolved successfully before timeout
        // Save last order state and open WhatsApp prompt only after mutation completes
        if (placeInfo?.redirect_to_whatsapp === true) {
          setLastOrderState(state);
          setOpenPromptSendOrderViaWhatsApp(true);
        }

        formik.setSubmitting(false);

        // Open video and navigate as before
        setOpenVideoDialog(true);
        setTimeout(() => toggleDrawer(), 1500);

        navigate({
          pathname: '/orders',
          search: searchParams.toString(),
        }, { replace: true });
      } catch (err) {
        // Handle errors (network / timeout)
        console.error('Failed to close order:', err);
        showSnackbarMessage({ message: 'Erro ao enviar pedido. Tente novamente.', severity: 'error' });
        // optional: still allow user to proceed or retry
        formik.setSubmitting(false);
      } finally {
        // Clean up the blocking UI and unload blocker
        window.removeEventListener('beforeunload', beforeUnload);
        setIsBlocking(false);
      }
     /*

      logger.log('vamos chamar closeUserOrder')
      await closeUserOrderAsync(notNullShoppingData);
      //closeUserOrder(notNullShoppingData);
      logger.log('já chamamos closeUserOrder')
      // obs: o di
      if(placeInfo?.redirect_to_whatsapp === true) {
        // se vamos perguntar sobre whatsapp, vamos salvar o state.
        // acho que não é o melhor/correto jeito de fazer, mas acho que vai funcionar..
        const state = JSON.stringify({
          placeInfo, selectedPaymentType, loginInfo, notNullShoppingData
        })
        setLastOrderState(state)

        logger.log('vamos chamar openPromptSendOrderViaWhatsApp')
        setOpenPromptSendOrderViaWhatsApp(true);
        logger.log('já chamamos chamar openPromptSendOrderViaWhatsApp')
      }

      formik.setSubmitting(false);

      // console.log(shoppingBag);
      logger.log('vamos abrir o video')
      setOpenVideoDialog(true);
      logger.log('já abrimos o video')

      setTimeout( () => toggleDrawer(), 1500);

      navigate({
        pathname: '/orders',
        search: searchParams.toString(),
      }, { replace: true });*/
    },
  });

  const checkIfShouldAskChangeValue = (value: string) => {
//console.log('value: ', Number(value))
   // console.log("VELHO VALOR DE TROCO: ", changeValue)
    //console.log('formik.values', formik.values)
    //console.log('paymentTypesQuery.data.data', paymentTypesQuery?.data?.data)

    const selectedPaymentType = paymentTypesQuery?.data?.data.find((type) => type.id === Number(value))

    //console.log('selectedPaymentType', selectedPaymentType)

    if(selectedPaymentType?.description.toLowerCase().includes('dinheiro')
      && shoppingBag?.total > 0) {
      setOpenChangeValueDialog(true)
    }
    else {
      setChangeValue(BigInt(0))
    }

    //console.log("NOVO VALOR DE TROCO: ", changeValue)
  }

  const handleAddQuantity = (pedido: IShoppingBagItem) => {
    if(shoppingBag === null) return

    const updateParams: IUpdateItemShoppingBag = {
      body: {
        quantity: pedido.quantity + 1,
        unit_price: pedido.total_price / pedido.quantity,
        flavour_control: pedido.flavour_control,
        observations: pedido.observations,
        total_price:
          (pedido.total_price / pedido.quantity) * (pedido.quantity + 1),
        compositionGroups: pedido.compositionGroups,
      },
      params: {
        itemId: pedido.id,
        orderId: shoppingBag!.id
      },
    };
    // console.log(updateParams);
    //console.log("handleAddQuantity")

    const obj = Object.fromEntries( searchParams.entries() );
    obj.shopping_bag_open = "true";
    setSearchParams(obj);

    updateItemShoppingBagMutate(updateParams);
  };

  const handleRemove = async (param: IShoppingBagItem, parametro?: string) => {
    if(shoppingBag === undefined) return

    logger.log('FUNCTION handleRemove do ShoppingBag')

    const shoppingBagAux = shoppingBag;
    if (parametro === "remover") {
      logger.log('parametro é remover, vamos executar....')
      shoppingBagAux.items = shoppingBag.items.filter(
        (item: IShoppingBagItem) => item.id !== param.id
      );

      const params: IShoppingBagRemoveItemParams = {
        item_id: param.id,
        order_id: shoppingBag.id,
      };
      removeItemShoppingBag(params);
      setTimeout(() => {
        logger.log('finalizou timeout do parametro remover....')
      }, 2000);

      logger.log('finalizou if do parametro remover.')
      return;
    }

    if (param.quantity === 1) {
      logger.log('param.quantity é 1....')
      shoppingBagAux.items = shoppingBag.items.filter(
        (item: IShoppingBagItem) => item.id !== param.id
      );

      const params: IShoppingBagRemoveItemParams = {
        item_id: param.id,
        order_id: shoppingBag.id,
      };
      removeItemShoppingBag(params);
      setTimeout(() => {
        logger.log('finalizou timeout do param.quantity é 1....')
      }, 2000);

      logger.log('finalizou if do param.quantity é 1....')
      return;
    }

    logger.log('não caiu em nenhum dos ifs anteriores')
    const updateParams: IUpdateItemShoppingBag = {
      body: {
        quantity: param.quantity - 1,
        unit_price: param.total_price / param.quantity,
        flavour_control: param.flavour_control,
        observations: param.observations,
        total_price:
          (param.total_price / param.quantity) * (param.quantity - 1),
        compositionGroups: param.compositionGroups,
      },
      params: {
        itemId: param.id,
        orderId: shoppingBag.id,
      },
    };
    updateItemShoppingBagMutate(updateParams);
  };

  const frete = formik.values.deliveryMode === 'retirada'
    ? (0.00)
    : shoppingBag?.delivery_tax

  const ehDeliveryEncomendaMasNaoSelecionouFormaDePagamento =
    (formik.values.payment_type === null || formik.values.payment_type < 1)
    && (shoppingBag?.table_number === null || shoppingBag?.table_number < 1);

  const ehDeliveryMasEstaForaAreaEntrega =
    formik.values.deliveryMode === 'delivery'
    && (shoppingBag.can_delivery_to_selected_address == 1 || shoppingBag.can_delivery_to_selected_address == 3);

  return (
    <>
      <Box p={2} minWidth={"360px"}>
        <Box padding="20px">
          <Typography
            color={Colors.baseTextColor}
            style={{ fontWeight: 300 }}
          >
            Seu pedido em
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {placeInfo?.name}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography
              color="primary"
              onClick={toggleDrawer}
              sx={{ cursor: "pointer" }}
            >
              Ver Cardápio
            </Typography>
          </Box>
          <Typography
            fontSize="14px"
            style={{
              fontWeight: 700,
              marginTop: "10px",
            }}
          >
            Produtos:
          </Typography>
          <Divider />
          {shoppingBag?.items.length ? (
            shoppingBag.items.map((pedido: IShoppingBagItem) => (
              <Box key={pedido.id} sx={{
                backgroundColor: (pedido.isNew ?? false) ? Colors.grayBackground : 'none',
                borderLeft: `3px solid ${(pedido.isNew ?? false) ? Colors.grayBackground : 'white'}`,
                borderRight: `3px solid ${(pedido.isNew ?? false) ? Colors.grayBackground : 'white'}`,
              }}>
                <Box
                  sx={{
                    display: "flex",
                    padding: "20px 0px 5px 0px",
                  }}
                >
                  <Typography
                    fontSize="18px"
                    style={{
                      fontWeight: 300,
                    }}
                    sx={{
                      maxWidth: "360px",
                    }}
                  >
                    {/* {pedido.quantity}x {pedido.productInfo.name.length <= 40 ? pedido.productInfo.name : (pedido.productInfo.name.substring(0, 40) + "...")} */}
                    {pedido.quantity}x {pedido.productInfo.name}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography
                    style={{
                      fontWeight: 700,
                    }}
                  >
                    {pedido.total_price.toLocaleString("pt-br", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </Typography>
                </Box>
                {pedido.observations && (
                  <Box paddingX={2}>
                    <Typography
                      color={Colors.grayTextColorDarkestDark}
                      fontSize={"12px"}
                    >
                      Obs: {pedido.observations}
                    </Typography>
                  </Box>
                )}
                <Box>
                  {pedido.compositionGroups.map((group) => {
                    return group.compositionGroupItems
                      .filter((item) => item.quantity > 0)
                      .map((opcional) => (
                        <Box
                          key={opcional.id}
                          display="flex"
                          sx={{
                            alignItems: "center",
                            paddingY: "2px",
                          }}
                        >
                          <Typography
                            fontSize={"13px"}
                            fontWeight={"700"}
                            color={"black"}
                            sx={{
                              paddingX: "7px",
                            }}
                          >
                            {opcional.quantity}
                          </Typography>
                          <Typography
                            color={Colors.grayTextColorDarkestDark}
                            fontSize={"11px"}
                            fontWeight={"500"}
                            sx={{
                              marginLeft: "3px",
                            }}
                          >
                            {opcional.name}
                          </Typography>
                        </Box>
                      ));
                  })}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    paddingBottom: "20px",
                  }}
                >
                  {(pedido.isNew ?? false) && <CircularProgress size={30} sx={{padding:'5px'}}/>}
                  { shoppingBag
                    && !(pedido.isNew ?? false)
                    &&
                    <EditItemBagDialog
                      originalProduct={ pedido }
                      shoppingBag={ shoppingBag }
                    />
                  }
                  {pedido.quantity > 1 && (
                    <IconButton
                      size="small"
                      disabled={isLoadingShoppingBag || (pedido.isNew ?? false)}
                      sx={{
                        paddingX: 1,
                      }}
                      onClick={() => handleRemove(pedido, "remover")}
                    >
                      <Delete
                        sx={{
                          width: "20px",
                          height: "20px",
                        }}
                      />
                    </IconButton>
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: isLoadingShoppingBag  || (pedido.isNew ?? false) ? "#EBEBE4" : "#FFF",
                      border: "1px solid grey",
                    }}
                  >
                    <IconButton
                      size="small"
                      disabled={isLoadingShoppingBag || (pedido.isNew ?? false)}
                      sx={{
                        paddingX: 1,
                      }}
                      onClick={() => handleRemove(pedido)}
                    >
                      {pedido.quantity === 1 ? (
                        <Delete
                          sx={{
                            width: "20px",
                            height: "20px",
                          }}
                          color={!isLoadingShoppingBag ? "error" : "inherit"}
                        />
                      ) : (
                        <Remove
                          sx={{
                            width: "20px",
                            height: "20px",
                          }}
                          color={!isLoadingShoppingBag ? "error" : "inherit"}
                        />
                      )}
                    </IconButton>
                    <Typography
                      fontWeight={"700"}
                      fontSize={"14px"}
                      sx={{
                        paddingX: 1,
                      }}
                    >
                      {pedido.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={isLoadingShoppingBag}
                      sx={{
                        paddingX: 1,
                      }}
                      onClick={() => handleAddQuantity(pedido)}
                    >
                      <Add
                        sx={{
                          width: "20px",
                          height: "20px",
                        }}
                        color={!isLoadingShoppingBag ? "error" : "inherit"}
                      />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <Box padding={2}>
              <Typography>Seu carrinho está vazio.</Typography>
            </Box>
          )}
          {!isLoadingShoppingBag && shoppingBag.items?.length > 0 && <Box><Typography color={'transparent'} fontSize={13}>Atualizando...</Typography></Box>}
          {isLoadingShoppingBag && <Box>
            <Typography color={"#AAA"} fontSize={13}>Atualizando... <CircularProgress sx={{ marginLeft: "15px" }}
                                                                                      size={13} /></Typography>
          </Box>}
          <Divider/>
          <Box
            sx={{
              display: "flex",
              padding: "15px 0px 5px 0px",
            }}
          >
            <Typography fontSize="16px" color={Colors.baseTextColor}>
              Subtotal
            </Typography>
            <Box sx={{ flexGrow: 1 }} />

            <Typography fontSize="16px" color={(isRefetchingShoppingBag ? Colors.grayTextColor2 : Colors.baseTextColor)}>
              {shoppingBag?.subtotal.toLocaleString("pt-br", {
                style: "currency",
                currency: "BRL",
              })}
              {isRefetchingShoppingBag && <CircularProgress size={14} sx={{position: 'relative', right: '-8px'}}/>}
            </Typography>
          </Box>
          <Divider />
          { shoppingBag.table_number > 0 ? (
            <Box
              sx={{
                display: "flex",
                padding: "20px 0px 5px 0px",
              }}
            >
              <Typography fontSize="16px" color={Colors.baseTextColor}>
                Você está pedindo na mesa
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Typography fontSize="16px" color={Colors.baseTextColor}>
                <strong>{shoppingBag?.table_number}</strong>
              </Typography>
              <Typography
                fontSize="16px"
                color={Colors.baseTextColor}
                onClick={() => setOpenEditTableNumberDialog(true)}
                sx={{
                  textDecoration: "underline",
                  marginLeft: "8px",
                  cursor: "pointer",
                }}
              >
                <strong>(Mudar)</strong>
              </Typography>
            </Box>
          ) : (
            ""
          )}




          {(shoppingBag?.delivery_tax > 0 || (shoppingBag?.table_number < 1)) && placeInfo?.only_withdraw !== true
            && (
            <Box
              sx={{
                display: "flex",
                padding: "20px 0px 5px 0px",
              }}
            >
              <Typography fontSize="16px" color={Colors.baseTextColor}>
                Frete
              </Typography>
              <Divider />
              <Box sx={{ flexGrow: 1 }} />
              <Typography fontSize="16px" color={isRefetchingShoppingBag ? Colors.grayTextColor2 : Colors.baseTextColor}>
                {
                  frete.toLocaleString("pt-br", {
                    style: "currency", currency: "BRL", })
                }
                {isRefetchingShoppingBag && <CircularProgress size={14} sx={{position: 'relative', right: '-8px'}}/>}
              </Typography>
            </Box>
          )}

          {shoppingBag.table_number < 1 && placeInfo?.only_withdraw !== true &&
            <>
              <Divider />
              <SelectDeliveryMode
                value={formik.values.deliveryMode}
                onChange={formik.handleChange}
              />
            </>}
          <Divider />
          {shoppingBag.table_number > 0 ? "" : (<Box
            sx={{
              padding: "0px 0px 0px 0px",
              alignItems: "center",
            }}
          >
            <Typography fontSize="16px" color={Colors.baseTextColor}>
              Forma de pagamento
            </Typography>
            <SelectPaymentType
              paymentTypesQuery={paymentTypesQuery}
              value={formik.values.payment_type}
              onChange={(event, value) => {

                formik.handleChange(event);
                checkIfShouldAskChangeValue(value)
              }
            }
              />
            {formik.errors.payment_type && (
              <Alert
                severity="warning"
                sx={{
                  display: "flex",
                  flexGrow: 1,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography fontSize="14px" fontWeight={700}>
                  {formik.errors.payment_type}
                </Typography>
              </Alert>
            )}
            {formik.values.payment_type < 1  && (
              <Alert
                severity="warning"
                sx={{
                  display: "flex",
                  flexGrow: 1,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography fontSize="11px" fontWeight={700}>
                  É necessario escolher uma forma de pagamento
                </Typography>
              </Alert>
            )}
          </Box>)}
        </Box>
      </Box>



      <Box p={3} sx={{ marginTop: { md: "auto", sm: "-40px" } }}>
        {shoppingBag?.table_number < 1 && placeInfo?.only_withdraw === true
          &&
          <Box
            sx={{
              display: "flex",
              textAlign: "center",
              padding: "20px 0px 5px 0px",
              marginBottom: "20px",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Typography fontSize="16px">
              Atenção: Retire seu pedido em nosso endereço
            </Typography>
            <Typography color={Colors.errorTextColor}>
              {placeInfo?.address_description}, {placeInfo?.address_number}<br />
              {placeInfo?.address_neighborhood}, {placeInfo?.address_city} - {placeInfo?.address_state}<br />
            </Typography>
          </Box>
        }

        <Box sx={{ display: "flex" }}>
          <Typography fontSize="16px" style={{ fontWeight: 700 }}>
            Total
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography fontSize="16px" style={{ fontWeight: 700 }} color={isRefetchingShoppingBag ? Colors.grayTextColor2 : Colors.baseTextColor}>
            {(shoppingBag?.total - (formik.values.deliveryMode === 'retirada' ? shoppingBag?.delivery_tax : 0))
              .toLocaleString("pt-br", {
                style: "currency",
                currency: "BRL",
              })}
            {isRefetchingShoppingBag && <CircularProgress size={14} sx={{position: 'relative', right: '-8px'}}/>}
          </Typography>
        </Box>
        { paymentTypesQuery?.data?.data.find((type) => type.id === Number(formik.values.payment_type))?.description.toLowerCase().includes('dinheiro')
            && (shoppingBag?.total > 0) &&
        <Box sx={{ display: "flex" }}>
          <Typography fontSize="16px" style={{ fontWeight: 700 }}>
            Troco para
          </Typography>
          <Button
            sx={{
              fontSize: "12px",
              paddingTop: "0px",
            }}
            style={{ color: Colors.baseTextColor }}
            disabled={isLoadingShoppingBag}
            onClick={() => setOpenChangeValueDialog(true)}
          >(alterar)</Button>
          <Box sx={{ flexGrow: 1 }} />

          <Typography fontSize="16px" style={{ fontWeight: 700 }} color={isRefetchingShoppingBag ? Colors.grayTextColor2 : Colors.baseTextColor}>
            {changeValue
              ? (Number(changeValue)/100)
              .toLocaleString("pt-br", {
                style: "currency",
                currency: "BRL",
              })
            : "Não precisa de troco" }
            {isRefetchingShoppingBag && <CircularProgress size={14} sx={{position: 'relative', right: '-8px'}}/>}
          </Typography>
        </Box>}

        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginTop: 1,
            }}
          >
            <Typography
              fontSize="16px"
              style={{ fontWeight: 400 }}
              color={Colors.baseTextColor}
            >
              CPF na nota
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <CpfTextField
              id="user_document"
              type="text"
              label="Digite o CPF"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.user_document}
              sx={{ marginTop: "5px" }}
              required={false}
            />
          </Box>
          {formik.errors.user_document && (
            <Alert
              severity="warning"
              sx={{
                display: "flex",
                flexGrow: 1,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography fontSize="14px" fontWeight={700}>
                {formik.errors.user_document}
              </Typography>
            </Alert>
          )}
          <Box sx={{ marginTop: "10px" }}>
            <Typography
              fontSize="16px"
              style={{ fontWeight: 400 }}
              color={Colors.baseTextColor}
            >
              Observações?
            </Typography>
            <TextField
              sx={{ marginTop: "5px" }}
              fullWidth
              id="observations"
              placeholder="Ex: Para viagem, comer no local."
              variant="standard"
              value={formik.values.observations}
              onChange={formik.handleChange}
            />
          </Box>
          {formik.values.deliveryMode === 'delivery' &&
            <DeliveryDistanceWarning can_delivery_to_selected_address={shoppingBag.can_delivery_to_selected_address}/>}

          <Button
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
            disabled={
              !formik.isValid
              || isLoadingShoppingBag
              || ehDeliveryEncomendaMasNaoSelecionouFormaDePagamento
              || shoppingBag?.items.length === 0
              || ehDeliveryMasEstaForaAreaEntrega
              || isMutatingRemoveItem
              || isMutatingUpdateItem
            }
            // onClick={() => handleCloseOrder()}
            sx={{ color: "white", marginTop: 2 }}
          >
            {(isLoadingShoppingBag
              || isMutatingRemoveItem
              || isMutatingUpdateItem)
              && (
              <CircularProgress size={20} sx={{marginRight: 2}} />
            )}
              Enviar Pedido
          </Button>
        </form>
      </Box>

      {isBlocking && (
        <Box    sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1400, // higher than drawer/modal
          background: 'rgba(100,100,100,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
        }}    aria-hidden="true"  >
          <Box sx={{ textAlign: 'center', background: 'white', padding: 4, borderRadius: 2 }}>
            <CircularProgress />
            <Typography mt={1}>Aguarde, não feche nem mude a janela. Enviando pedido...</Typography>
          </Box>
        </Box>
      )}

    </>
  )
}